/* istanbul ignore file */
import { useEffect, useState } from 'react';
import { useFormContext, useInputHelper } from 'framework/form';
import useJournalEntryService from 'domain/finance/journalEntry/useJournalEntryService';
import { InputLoader } from 'framework/ui/mui-builder';
import { useDateService, DATE_FORMAT } from 'framework/utils';
import { get, isEmpty } from 'lodash';
import { Box, Typography } from '@mui/material';

const DEFAULT_EXCHANGE_RATE = '1.00';
const DEFAULT_FUNCTION = () => {};

const ExchangeRateFormField = ({ dateSource }: { dateSource: string }) => {
  const { setSubmitEnabled = DEFAULT_FUNCTION } = useFormContext();
  const { getRawOnChangeCallback, getSource, getValue } = useInputHelper({ source: 'exchangeRates' });
  const { data } = useFormContext();
  const { formatDate, getToday } = useDateService();
  const { getExchangeRates } = useJournalEntryService();
  const today = getToday();
  const date = get(data, dateSource);
  const source = getSource();
  const value = getValue() as any;
  const onChangeCallback = getRawOnChangeCallback();
  const [loading, setLoading] = useState(true);
  const [hasExchangeRates, setHasExchangeRates] = useState(true);

  const loadData = async () => {
    try {
      setSubmitEnabled(false);
      setLoading(true);
      const requestDate = date || formatDate(today, DATE_FORMAT.API_ONLY_DATE);
      const result = await getExchangeRates(date || formatDate(today, DATE_FORMAT.API_ONLY_DATE));

      if (onChangeCallback) {
        onChangeCallback({
          [source]: {
            date: requestDate,
            ...result,
          },
        });
        setHasExchangeRates(!isEmpty(result));
      }
    } catch (error) {
      throw new Error(error as string);
    } finally {
      setLoading(false);
      setSubmitEnabled(true);
    }
  };

  useEffect(() => {
    loadData();
    // Suppressed warnings because we only need to call useEffect callback after updating date.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return (
    <Box
      sx={{
        paddingBottom: '20px',
      }}
    >
      <Typography variant="subtitle1">Exchange rate for {formatDate(date || today, DATE_FORMAT.FULL_DATE)}:</Typography>
      {loading && (
        <div>
          <InputLoader />
        </div>
      )}

      {!loading && !hasExchangeRates && (
        <Typography variant="subtitle1">
          There are no exchange rates for {formatDate(date || today, DATE_FORMAT.FULL_DATE)}
        </Typography>
      )}

      {!loading && hasExchangeRates && (
        <>
          <Typography variant="body1">1$ = {value?.PLN?.USD || DEFAULT_EXCHANGE_RATE}zł.</Typography>
          <Typography variant="body1">1$ = {value?.BYN?.USD || value?.BYR?.USD || DEFAULT_EXCHANGE_RATE}Br</Typography>
        </>
      )}
    </Box>
  );
};

export default ExchangeRateFormField;
